import type {
  FactFamilyId,
  FactId,
  SkillId,
  StrategyId,
  SupportLevel,
  ResponseMode,
} from "../model.ts";

export const LEARNING_OBJECTIVES = [
  "CALIBRATE",
  "LEARN_CONCEPT",
  "LEARN_STRATEGY",
  "PRACTICE_STRATEGY",
  "AUTOMATE",
  "MEMORIZE",
  "MAINTAIN",
  "TRANSFER",
] as const;
export type LearningObjective = (typeof LEARNING_OBJECTIVES)[number];

export const SELECTION_REASONS = [
  "NEW_SKILL",
  "SKILL_BUILDING",
  "WEAK_FACT",
  "PREREQUISITE_REPAIR",
  "DUE_REVIEW",
  "MAINTENANCE",
  "TRANSFER_PROBE",
  "SUCCESS_BALANCE",
  "STRATEGY_INTRODUCTION",
  "STRATEGY_GUIDED_PRACTICE",
  "STRATEGY_INDEPENDENT_CHECK",
  "STRATEGY_REFRESH",
] as const;
export type SelectionReason = (typeof SELECTION_REASONS)[number];

export type Fluency = "FLUENT" | "SLOW" | "UNASSESSED" | "INVALID";

export interface Attempt {
  readonly id: string;
  readonly profileId: string;
  readonly presentationId: string;
  readonly attemptOrdinal: number;
  readonly sessionId: string;
  readonly factId: FactId;
  readonly factFamilyId: FactFamilyId | null;
  readonly targetSkillId: SkillId;
  readonly objective: LearningObjective;
  readonly responseMode: ResponseMode;
  readonly supportLevel: SupportLevel;
  readonly supportUsed: boolean;
  readonly presentedAt: string;
  readonly firstInputAt: string | null;
  readonly submittedAt: string;
  readonly enteredAnswer: number | null;
  readonly correct: boolean | null;
  readonly editCount: number;
  readonly helpRequested: boolean;
  readonly teachingStrategyShown: StrategyId | null;
  readonly strategyId: StrategyId | null;
  readonly selectionReason: SelectionReason;
  readonly catalogVersion: string;
  readonly rulesetVersion: string;
  readonly fluency: Fluency;
  readonly fluencyPolicyVersion: string | null;
}

export interface AttemptTiming {
  readonly thinkingTimeMs: number | null;
  readonly interactionTimeMs: number | null;
  readonly totalTimeMs: number;
}

export interface AttemptValidationIssue {
  readonly code: string;
  readonly message: string;
}

function milliseconds(timestamp: string): number {
  return Date.parse(timestamp);
}

export function attemptTiming(attempt: Attempt): AttemptTiming {
  const presentedAt = milliseconds(attempt.presentedAt);
  const submittedAt = milliseconds(attempt.submittedAt);
  const firstInputAt = attempt.firstInputAt === null
    ? null
    : milliseconds(attempt.firstInputAt);

  return {
    thinkingTimeMs: firstInputAt === null ? null : Math.max(0, firstInputAt - presentedAt),
    interactionTimeMs: firstInputAt === null ? null : Math.max(0, submittedAt - firstInputAt),
    totalTimeMs: Math.max(0, submittedAt - presentedAt),
  };
}

export function validateAttempt(attempt: Attempt): AttemptValidationIssue[] {
  const issues: AttemptValidationIssue[] = [];
  const presentedAt = milliseconds(attempt.presentedAt);
  const submittedAt = milliseconds(attempt.submittedAt);
  const firstInputAt = attempt.firstInputAt === null
    ? null
    : milliseconds(attempt.firstInputAt);

  if (![presentedAt, submittedAt].every(Number.isFinite)) {
    issues.push({ code: "INVALID_TIMESTAMP", message: "presentedAt en submittedAt moeten geldige ISO-tijden zijn." });
  } else if (submittedAt < presentedAt) {
    issues.push({ code: "TIMESTAMP_ORDER", message: "submittedAt mag niet vóór presentedAt liggen." });
  }

  if (firstInputAt !== null) {
    if (!Number.isFinite(firstInputAt)) {
      issues.push({ code: "INVALID_FIRST_INPUT", message: "firstInputAt moet null of een geldige ISO-tijd zijn." });
    } else if (firstInputAt < presentedAt || firstInputAt > submittedAt) {
      issues.push({ code: "FIRST_INPUT_ORDER", message: "firstInputAt moet tussen presentedAt en submittedAt liggen." });
    }
  }

  if (!Number.isInteger(attempt.attemptOrdinal) || attempt.attemptOrdinal < 1) {
    issues.push({ code: "INVALID_ATTEMPT_ORDINAL", message: "attemptOrdinal moet bij 1 beginnen." });
  }
  if (!Number.isInteger(attempt.editCount) || attempt.editCount < 0) {
    issues.push({ code: "INVALID_EDIT_COUNT", message: "editCount moet een niet-negatief geheel getal zijn." });
  }
  if (!attempt.helpRequested && attempt.enteredAnswer === null) {
    issues.push({ code: "MISSING_ANSWER", message: "Een submit zonder hulpvraag moet een ingevoerd antwoord bevatten." });
  }
  if (attempt.helpRequested && attempt.correct !== null) {
    issues.push({
      code: "HELP_IS_NOT_ANSWER",
      message: "Een hulpvraag is geen correct of incorrect antwoord en krijgt correct=null.",
    });
  }
  if (!attempt.helpRequested && attempt.correct === null) {
    issues.push({
      code: "ANSWER_NEEDS_CORRECTNESS",
      message: "Een gegeven antwoord moet als correct of incorrect beoordeeld zijn.",
    });
  }
  if (attempt.responseMode === "DIRECT_RETRIEVAL") {
    if (attempt.supportLevel !== "NONE" || attempt.supportUsed || attempt.teachingStrategyShown !== null) {
      issues.push({
        code: "INVALID_DIRECT_RETRIEVAL",
        message: "DIRECT_RETRIEVAL mag geen ondersteuning of zichtbare strategie bevatten.",
      });
    }
  }
  if (attempt.fluency !== "UNASSESSED" && attempt.fluencyPolicyVersion === null) {
    issues.push({
      code: "MISSING_FLUENCY_POLICY",
      message: "Beoordeelde fluency moet naar een policyversie verwijzen.",
    });
  }

  return issues;
}

export function isIndependentEvidence(attempt: Attempt): boolean {
  return attempt.correct === true
    && !attempt.helpRequested
    && !attempt.supportUsed
    && (attempt.supportLevel === "AVAILABLE" || attempt.supportLevel === "NONE")
    && (attempt.responseMode === "NUMERIC_ENTRY" || attempt.responseMode === "DIRECT_RETRIEVAL");
}

export function isValidDirectRetrieval(attempt: Attempt): boolean {
  return attempt.responseMode === "DIRECT_RETRIEVAL"
    && attempt.correct === true
    && !attempt.helpRequested
    && !attempt.supportUsed
    && attempt.supportLevel === "NONE"
    && attempt.teachingStrategyShown === null
    && attempt.fluency === "FLUENT";
}

export function isCleanDirectRetrieval(attempt: Attempt): boolean {
  return isValidDirectRetrieval(attempt) && attempt.editCount === 0;
}

export function isIndependentStrategyEvidence(attempt: Attempt): boolean {
  return attempt.strategyId !== null
    && isIndependentEvidence(attempt)
    && attempt.responseMode === "NUMERIC_ENTRY";
}
